import { useEffect, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { timestampMs } from '@bufbuild/protobuf/wkt'
import { useQuery } from '@connectrpc/connect-query'
import { useStore } from '@tanstack/react-store'

import { listMySupportSessions } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListMySupportSessionsRequestSchema, type SupportSession } from '@/gen/cortado/v1/admin_pb'
import { authStore } from '@/stores/auth.store'
import { isDemoModeActive } from '@/lib/demo-mode'

const REFETCH_INTERVAL_MS = 60_000

/** Expiry instant of a session in epoch milliseconds, or 0 when unset. */
export function supportSessionExpiresAtMs(session: SupportSession): number {
  return session.expiresAt ? timestampMs(session.expiresAt) : 0
}

function isLive(session: SupportSession, nowMs: number): boolean {
  return session.active && supportSessionExpiresAtMs(session) > nowMs
}

/**
 * The caller's live superadmin support sessions (ListMySupportSessions). For
 * everyone but a Cortado superadmin this is always empty.
 *
 * A session drops out at its expires_at even between refetches: a timer
 * re-renders at the earliest expiry so the banner and permission set flip on
 * time instead of up to a minute late.
 */
export function useMySupportSessions() {
  const session = useStore(authStore, (state) => state.session)
  const enabled = !!session && !isDemoModeActive()

  const query = useQuery(listMySupportSessions, create(ListMySupportSessionsRequestSchema, {}), {
    enabled,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  })

  const [nowMs, setNowMs] = useState(() => Date.now())
  const sessions = (query.data?.sessions ?? []).filter((s) => isLive(s, nowMs))

  const nextExpiryMs = sessions.reduce<number | null>((earliest, s) => {
    const expiresAt = supportSessionExpiresAtMs(s)
    return earliest === null || expiresAt < earliest ? expiresAt : earliest
  }, null)

  useEffect(() => {
    if (nextExpiryMs === null) return
    const timer = window.setTimeout(() => setNowMs(Date.now()), Math.max(0, nextExpiryMs - Date.now()) + 50)
    return () => window.clearTimeout(timer)
  }, [nextExpiryMs])

  return { sessions, isLoading: query.isLoading }
}

/** The caller's live support session for `organizationId`, or null. */
export function useSupportSession(organizationId: string): SupportSession | null {
  const { sessions } = useMySupportSessions()
  if (!organizationId) return null
  return sessions.find((s) => s.organizationId === organizationId) ?? null
}
